import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale, useNotify } from 'react-admin';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Eye,
  Loader2,
  PenLine,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiFetchResponse } from '../lib/apiFetch';
import { langOf } from './siedStatus';
import {
  defaultSigner,
  fill,
  formatWhen,
  Lang,
  PILL_LABELS,
  SIED_BASE_URL,
  SIGN_LABELS as T,
  SignerName,
  signerLabel,
  SignPreview,
  SignStateResponse,
  sidebarPill,
} from './siedSign';

/** JSON from the backend, throwing the server's own message on failure. */
async function api<R>(path: string, init: RequestInit = {}): Promise<R> {
  const res = await apiFetchResponse(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = body?.message;
    throw new Error(
      Array.isArray(msg) ? msg.join(' ') : msg || `API error: ${res.status}`
    );
  }
  return body as R;
}

const POLL_MS = 2500;

/** Sign state for a case, re-read every few seconds while a run is going. */
export function useSiedSignState(
  funeralId: number | undefined,
  onFinished?: () => void
) {
  const [state, setState] = useState<SignStateResponse | null>(null);

  const reload = useCallback(async () => {
    if (!funeralId) return;
    try {
      setState(await api<SignStateResponse>(`sied/sign/state/${funeralId}`));
    } catch {
      setState(null);
    }
  }, [funeralId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const running = state?.eligibility.state === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => void reload(), POLL_MS);
    return () => clearInterval(t);
  }, [running, reload]);

  // A run just finished: let the caller pick up the new SIED status.
  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (running) setWasRunning(true);
    else if (wasRunning) {
      setWasRunning(false);
      onFinished?.();
    }
  }, [running, wasRunning, onFinished]);

  return { state, reload };
}

const NOTE = 'text-[11px] leading-snug text-muted-foreground';
const ACTION_BTN = 'h-6 gap-1 px-2 text-[11px]';

/**
 * Right side of the sidebar SIED row, like the other Status rows: one pill
 * (blocked reasons and SIED's own text in its tooltip) and at most one small
 * action — Sign / Retry open the confirmation dialog, the eye on a signed form
 * opens a read-only details dialog with the proof. When and who live in the
 * case feed.
 */
export const SiedSidebarControls: React.FC<{
  funeralId: number;
  siedStatus?: string | null;
  siedRaw?: string | null;
  checkedLine?: string;
  deceasedName?: string;
  onChanged?: () => void;
}> = ({
  funeralId,
  siedStatus,
  siedRaw,
  checkedLine,
  deceasedName,
  onChanged,
}) => {
  const lang = langOf(useLocale());
  const { state, reload } = useSiedSignState(funeralId, onChanged);
  const [open, setOpen] = useState<null | 'sign' | 'details'>(null);
  const pill = sidebarPill(
    state,
    { status: siedStatus, raw: siedRaw, checkedLine },
    lang
  );

  return (
    <>
      <span
        title={pill.tip}
        data-sied-pill={pill.key}
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
          pill.cls
        )}
      >
        {pill.spinner && <Loader2 className="h-3 w-3 animate-spin" />}
        {pill.label}
      </span>
      {(pill.action === 'sign' || pill.action === 'retry') && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={ACTION_BTN}
          onClick={(e) => {
            e.stopPropagation();
            setOpen('sign');
          }}
        >
          <PenLine className="h-3 w-3" />
          {PILL_LABELS[pill.action][lang]}
        </Button>
      )}
      {pill.action === 'details' && (
        <button
          type="button"
          title={PILL_LABELS.details[lang]}
          aria-label={PILL_LABELS.details[lang]}
          onClick={(e) => {
            e.stopPropagation();
            setOpen('details');
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
      )}
      {open === 'sign' && (
        <SiedSignDialog
          funeralId={funeralId}
          deceasedName={deceasedName}
          lang={lang}
          onClose={() => setOpen(null)}
          onStarted={() => {
            setOpen(null);
            void reload();
          }}
        />
      )}
      {open === 'details' && state && (
        <SiedSignDetailsDialog
          state={state}
          lang={lang}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
};

/** Read-only: who signed, when, who clicked, what was entered, the proof. */
export const SiedSignDetailsDialog: React.FC<{
  state: SignStateResponse;
  lang: Lang;
  onClose: () => void;
}> = ({ state, lang, onClose }) => {
  const run = state.sign;
  const ev = (run?.enteredValues ?? {}) as Record<string, any>;
  const rows: Array<[string, React.ReactNode]> = [
    [T.signedBy[lang], run?.signerName || '—'],
    [
      lang === 'fr' ? 'Signé le' : 'Signed on',
      run?.signedAt ? formatWhen(run.signedAt, lang) : '—',
    ],
    [T.clickedBy[lang], run?.signedByName || '—'],
    [T.datePec[lang], ev.date_prise_en_charge || state.transportDate || '—'],
    [T.formNo[lang], state.formNumber || state.sp3Number || '—'],
    [T.siedStatus[lang], state.siedStatusRaw || '—'],
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            {T.signedInSied[lang]}
          </DialogTitle>
          <DialogDescription>
            {lang === 'fr'
              ? 'Disposition du corps signée dans SIED. Chaque tentative figure aussi au fil du dossier.'
              : 'Disposition du corps is signed in SIED. Every attempt is also in the case feed.'}
          </DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[minmax(0,9rem)_1fr] gap-x-3 gap-y-1.5 text-[13px]">
          {rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="min-w-0 break-words">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {state.siedId && (
            <a
              href={`${SIED_BASE_URL}/Deces/Verification/${state.siedId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1 rounded-md px-3 text-[12px] text-muted-foreground hover:bg-muted"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {T.openInSied[lang]}
            </a>
          )}
          {state.proofUrl && (
            <a
              href={state.proofUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1 rounded-md border px-3 text-[12px] font-medium hover:bg-muted"
            >
              {T.viewProof[lang]}
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Confirmation dialog ─────────────────────────────────────────────────────

const ok = (good: boolean, lang: Lang) => (
  <span
    className={cn(
      'whitespace-nowrap text-[11px] font-medium',
      good ? 'text-green-700' : 'text-red-700'
    )}
  >
    {good ? `✓ ${T.match[lang]}` : `✗ ${T.mismatch[lang]}`}
  </span>
);

const FIELD_LABEL: Record<
  SignPreview['mismatches'][number]['field'],
  Record<Lang, string>
> = {
  formNumber: T.formNo,
  lastName: { en: 'Last name', fr: 'Nom de famille' },
  firstName: { en: 'First name', fr: 'Prénom' },
  dateOfDeath: T.dateOfDeath,
};

const nameOf = (
  f:
    | {
        lastName: string | null;
        firstName: string | null;
        maidenName?: string | null;
      }
    | null
    | undefined,
  lang: Lang
) =>
  f
    ? [f.lastName?.toUpperCase(), f.firstName].filter(Boolean).join(', ') +
      (f.maidenName ? ` (née ${f.maidenName.toUpperCase()})` : '')
    : '';

export const SiedSignDialog: React.FC<{
  funeralId: number;
  deceasedName?: string;
  lang: Lang;
  onClose: () => void;
  onStarted: () => void;
  /** For screenshots/tests: skip the live read and show this. */
  initialPreview?: SignPreview;
}> = ({
  funeralId,
  deceasedName,
  lang,
  onClose,
  onStarted,
  initialPreview,
}) => {
  const notify = useNotify();
  const [preview, setPreview] = useState<SignPreview | null>(
    initialPreview ?? null
  );
  const [error, setError] = useState<string | null>(null);
  const [signer, setSigner] = useState<SignerName | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState<false | 'save_only' | 'sign'>(false);

  useEffect(() => {
    if (initialPreview) return;
    let live = true;
    api<SignPreview>(`sied/sign/preview/${funeralId}`)
      .then((p) => live && setPreview(p))
      .catch((err: Error) => live && setError(err.message));
    return () => {
      live = false;
    };
  }, [funeralId, initialPreview]);

  useEffect(() => {
    if (preview && !signer) setSigner(defaultSigner(preview.toEnter.signers));
  }, [preview, signer]);

  const mismatch = useMemo(
    () => new Set(preview?.mismatches.map((m) => m.field) ?? []),
    [preview]
  );
  const title =
    deceasedName ||
    [preview?.cleo.firstName, preview?.cleo.lastName].filter(Boolean).join(' ');
  const date = preview?.toEnter.date ?? '';
  const canGo =
    !!preview?.canProceed && !!signer && !!date && confirmed && !busy;

  const start = async (mode: 'save_only' | 'sign') => {
    if (!signer) return;
    setBusy(mode);
    try {
      await api('sied/sign', {
        method: 'POST',
        body: JSON.stringify({
          funeralId,
          mode,
          signerFirst: signer.first,
          signerLast: signer.last,
          confirmed: true,
        }),
      });
      onStarted();
    } catch (err) {
      notify(`${T.startFailed[lang]}: ${(err as Error).message}`, {
        type: 'error',
      });
      setBusy(false);
    }
  };

  const s = preview?.sied;
  const statusOk =
    !!s &&
    (s.status === 'certifier_signed_not_validated' ||
      s.status === 'certifier_signed_validated');

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {T.dialogTitle[lang]} {title}?
          </DialogTitle>
          <DialogDescription>{T.dialogSub[lang]}</DialogDescription>
        </DialogHeader>

        {!preview && !error && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {T.reading[lang]}
          </div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {T.previewFailed[lang]}: {error}
          </div>
        )}

        {preview && (
          <div className="space-y-4 text-sm">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 pr-2 font-medium">{T.field[lang]}</th>
                  <th className="py-1.5 pr-2 font-medium">
                    {T.cleoRecord[lang]}
                  </th>
                  <th className="py-1.5 pr-2 font-medium">{T.siedNow[lang]}</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-1.5 pr-2 text-muted-foreground">
                    {T.formNo[lang]}
                  </td>
                  <td className="py-1.5 pr-2 font-mono">
                    {preview.cleo.formNumber}
                  </td>
                  <td className="py-1.5 pr-2 font-mono">
                    {s?.formNumber ?? '—'}
                  </td>
                  <td className="py-1.5 text-right">
                    {s && ok(!mismatch.has('formNumber'), lang)}
                  </td>
                </tr>
                <tr
                  className={cn(
                    'border-b',
                    (mismatch.has('lastName') || mismatch.has('firstName')) &&
                      'bg-red-50'
                  )}
                >
                  <td className="py-1.5 pr-2 text-muted-foreground">
                    {T.deceased[lang]}
                  </td>
                  <td className="py-1.5 pr-2">{nameOf(preview.cleo, lang)}</td>
                  <td className="py-1.5 pr-2">{s ? nameOf(s, lang) : '—'}</td>
                  <td className="py-1.5 text-right">
                    {s &&
                      ok(
                        !mismatch.has('lastName') && !mismatch.has('firstName'),
                        lang
                      )}
                  </td>
                </tr>
                <tr
                  className={cn(
                    'border-b',
                    mismatch.has('dateOfDeath') && 'bg-red-50'
                  )}
                >
                  <td className="py-1.5 pr-2 text-muted-foreground">
                    {T.dateOfDeath[lang]}
                  </td>
                  <td className="py-1.5 pr-2">{preview.cleo.dateOfDeath}</td>
                  <td className="py-1.5 pr-2">{s?.dateOfDeath ?? '—'}</td>
                  <td className="py-1.5 text-right">
                    {s && ok(!mismatch.has('dateOfDeath'), lang)}
                  </td>
                </tr>
                <tr>
                  <td className="py-1.5 pr-2 text-muted-foreground">
                    {T.siedStatus[lang]}
                  </td>
                  <td className="py-1.5 pr-2 text-muted-foreground">—</td>
                  <td className="py-1.5 pr-2 text-[12px]">
                    {s?.statusRaw ?? '—'}
                  </td>
                  <td className="py-1.5 text-right">
                    {s && (
                      <span
                        className={cn(
                          'whitespace-nowrap text-[11px] font-medium',
                          statusOk ? 'text-green-700' : 'text-red-700'
                        )}
                      >
                        {statusOk ? `✓ ${T.ourTurn[lang]}` : '✗'}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            {(preview.mismatches.length > 0 ||
              preview.problems.length > 0 ||
              preview.alreadySigned) && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">
                <p className="flex items-center gap-1.5 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  {T.cantSign[lang]}
                </p>
                {preview.alreadySigned && <p>{T.alreadySigned[lang]}</p>}
                {preview.mismatches.map((m) => (
                  <p key={m.field}>
                    {FIELD_LABEL[m.field][lang]}: Cleo “{m.cleo ?? ''}” · SIED “
                    {m.sied ?? ''}”
                  </p>
                ))}
                {preview.problems.map((p) => (
                  <p key={p}>{p}</p>
                ))}
                {preview.mismatches.length > 0 && (
                  <p className="mt-1 text-red-700/80">{T.mismatchHelp[lang]}</p>
                )}
              </div>
            )}

            <div>
              <p className="mb-1.5 text-[12px] font-semibold">
                {T.willEnter[lang]}
              </p>
              <dl className="grid grid-cols-[minmax(0,11rem)_1fr_auto] items-center gap-x-3 gap-y-1.5 text-[13px]">
                <dt className="text-muted-foreground">
                  {T.representative[lang]}
                </dt>
                <dd>
                  <select
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-[13px]"
                    value={signer ? signerLabel(signer) : ''}
                    onChange={(ev) =>
                      setSigner(
                        preview.toEnter.signers.find(
                          (x) => signerLabel(x) === ev.target.value
                        ) ?? null
                      )
                    }
                    aria-label={T.representative[lang]}
                  >
                    {preview.toEnter.signers.map((x) => (
                      <option key={signerLabel(x)} value={signerLabel(x)}>
                        {x.last}, {x.first}
                      </option>
                    ))}
                  </select>
                </dd>
                <dd className={NOTE}>{T.fromSettings[lang]}</dd>

                <dt className="text-muted-foreground">{T.site[lang]}</dt>
                <dd className="text-[12px]">{preview.toEnter.siteLabel}</dd>
                <dd className={NOTE}>{T.fixed[lang]}</dd>

                <dt className="text-muted-foreground">{T.datePec[lang]}</dt>
                <dd className="font-mono">{date || '—'}</dd>
                <dd className={NOTE}>{T.transportDispatched[lang]}</dd>

                <dt className="text-muted-foreground">{T.mode[lang]}</dt>
                <dd>☑ {T.cremation[lang]}</dd>
                <dd className={NOTE}>{T.fixed[lang]}</dd>

                <dt className="text-muted-foreground">{T.permit[lang]}</dt>
                <dd className="text-[12px]">{T.leftAsIs[lang]}</dd>
                <dd className={NOTE}>{T.notTouched[lang]}</dd>
              </dl>
              {preview.current &&
                (preview.current.nom || preview.current.datePriseEnCharge) && (
                  <p className={cn(NOTE, 'mt-1.5')}>
                    {T.siedHasNow[lang]}:{' '}
                    {[preview.current.prenom, preview.current.nom]
                      .filter(Boolean)
                      .join(' ')}
                    {preview.current.datePriseEnCharge
                      ? ` · ${preview.current.datePriseEnCharge}`
                      : ''}
                    {preview.current.dispositions.length
                      ? ` · ${preview.current.dispositions.join(', ')}`
                      : ''}
                  </p>
                )}
            </div>

            <div className="space-y-1 rounded-md bg-muted/50 p-2.5 text-[12px] leading-snug text-muted-foreground">
              <p>{T.step1[lang]}</p>
              <p>{T.step2[lang]}</p>
            </div>

            <label className="flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={confirmed}
                disabled={!preview.canProceed}
                onChange={(ev) => setConfirmed(ev.target.checked)}
              />
              <span>
                {fill(T.confirm[lang], {
                  signer: signer ? signerLabel(signer) : '…',
                  date: date || '…',
                })}
              </span>
            </label>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={!!busy}
              >
                {T.cancel[lang]}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canGo}
                onClick={() => void start('save_only')}
              >
                {busy === 'save_only' && (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                )}
                {T.saveOnly[lang]}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canGo}
                onClick={() => void start('sign')}
              >
                {busy === 'sign' ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <PenLine className="mr-1 h-3.5 w-3.5" />
                )}
                {T.button[lang]}
              </Button>
            </div>
            <p className={NOTE}>{T.saveOnlyHelp[lang]}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SiedSidebarControls;
